import { dispatch, store } from 'store';
import { fetcher } from 'utils/axios';
import {
  setAccountModulesError,
  setAccountModulesLoading,
  setAccountModulesSuccess,
} from 'store/slices/accountModules';
import {
  ACCOUNT_MODULE_IDS,
  OPTIONAL_ACCOUNT_MODULE_IDS,
  ACCOUNT_SYSTEM_STATUSES,
  type AccountModuleId,
  type AccountModulesPayload,
  type AccountSystemStatus,
} from 'types/account/accountModules';
import { parseMessagesQuota } from 'types/account/messagesQuota';
import {
  parsePaymentMethod,
  parseSubscriptionPlan,
  parseUsageQuota,
} from 'types/account/subscriptionDetails';

/** נתיב הסטטוס בשרת הנוד — מחליף את ה-webhook של Make (שהחזיר נתוני דמו סטטיים). */
export const ACCOUNT_STATUS_PATH = '/api/account/status';

function isTruthyFlag(value: unknown): boolean {
  return value === true || value === 'true' || value === 1 || value === '1';
}

function parseSystemStatus(value: unknown): AccountSystemStatus | null {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toLowerCase();
  return ACCOUNT_SYSTEM_STATUSES.includes(normalized as AccountSystemStatus)
    ? (normalized as AccountSystemStatus)
    : null;
}

function parseTrialEndDate(value: unknown): string | null {
  if (value == null || value === '') return null;
  if (typeof value === 'string') return value.trim() || null;
  return null;
}

function parseAccountModulesPayload(raw: unknown): AccountModulesPayload | null {
  if (!raw || typeof raw !== 'object') return null;

  const root = raw as Record<string, unknown>;
  const systemStatus = parseSystemStatus(root.systemStatus);
  if (!systemStatus) return null;

  const trialEndDate = parseTrialEndDate(root.trialEndDate);

  const moduleSource =
    root.modules && typeof root.modules === 'object'
      ? (root.modules as Record<string, unknown>)
      : root;

  const modules = {} as Record<AccountModuleId, boolean>;
  for (const id of ACCOUNT_MODULE_IDS) {
    if (!(id in moduleSource)) {
      if (OPTIONAL_ACCOUNT_MODULE_IDS.has(id)) {
        modules[id] = false;
        continue;
      }
      return null;
    }
    modules[id] = isTruthyFlag(moduleSource[id]);
  }

  const messagesQuota = parseMessagesQuota(root.messagesQuota);
  const subscription = parseSubscriptionPlan(root.subscription);
  const paymentMethod = parsePaymentMethod(root.paymentMethod);
  const contactsQuota = parseUsageQuota(root.contactsQuota);
  const botsQuota = parseUsageQuota(root.botsQuota);
  const broadcastsQuota = parseUsageQuota(root.broadcastsQuota);

  const rawListMax = Number(root.distributionListMaxSize);
  const distributionListMaxSize =
    Number.isFinite(rawListMax) && rawListMax >= 0 ? rawListMax : null;

  const blockedReason =
    root.blockedReason === 'TRIAL_EXPIRED' || root.blockedReason === 'INACTIVE'
      ? root.blockedReason
      : null;

  return {
    modules,
    blockedReason,
    systemStatus,
    trialEndDate,
    messagesQuota,
    subscription,
    paymentMethod,
    contactsQuota,
    botsQuota,
    broadcastsQuota,
    distributionListMaxSize,
  };
}

/** Loads account status (modules/subscription/quotas) from the node server — called once after company bootstrap. */
export async function fetchAccountModules(): Promise<AccountModulesPayload | null> {
  const companyId =
    store.getState().company?.CompanyId ?? localStorage.getItem('companyId') ?? '';

  if (!companyId) return null;

  dispatch(setAccountModulesLoading());

  try {
    const res = (await fetcher('get', ACCOUNT_STATUS_PATH, 'servernode')) as Record<string, unknown> | null;

    // מעטפת הנוד: { apiVer, success, data, correlationId } — התוכן תחת data
    const payload = res && typeof res === 'object' && 'data' in res ? res.data : res;
    const parsed = parseAccountModulesPayload(payload);

    if (!parsed) {
      dispatch(setAccountModulesError());
      return null;
    }

    dispatch(setAccountModulesSuccess(parsed));
    return parsed;
  } catch {
    dispatch(setAccountModulesError());
    return null;
  }
}
